/**
 * ExperimentPicker
 * ----------------
 *
 * September 2026
 *
 * Lets the user pick which completed experiment to run DOE analysis on,
 * from every experiment available (GET /api/doe/experiments) rather than
 * only whatever was just run in this browser session. When there are more
 * than RECENT_EXPERIMENT_LIMIT experiments, defaults to showing only the
 * ones run in the last 24 hours, with a toggle to see everything.
 */
import * as React from "react";
import axios from "axios";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

const RECENT_EXPERIMENT_LIMIT = 20;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function formatOption(experiment) {
  const when = experiment.date ? new Date(experiment.date).toLocaleString() : "unknown date";
  return {
    _id: experiment._id,
    label: `${experiment.name || "(unnamed)"} -- ${experiment.model_name || "?"} -- ${when}`,
  };
}

function ExperimentPicker({ initialExperimentId, onSelect }) {
  const [experiments, setExperiments] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState(initialExperimentId || null);
  const [showAll, setShowAll] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    axios
      .get("/api/doe/experiments")
      .then((res) => {
        const list = res.data || [];
        setExperiments(list);
        setShowAll(list.length <= RECENT_EXPERIMENT_LIMIT);
        // Prefer whatever was just run this session; otherwise the most recent experiment.
        const preselected = initialExperimentId || list[0]?._id || null;
        setSelectedId(preselected);
        if (preselected) onSelect(preselected, list.find((e) => e._id === preselected));
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to load experiments.");
      })
      .finally(() => setLoading(false));
    // Only load once on mount -- initialExperimentId/onSelect are read at that point.
  }, []);

  const recentExperiments = React.useMemo(
    () => experiments.filter((e) => e.date && Date.now() - new Date(e.date).getTime() <= ONE_DAY_MS),
    [experiments],
  );

  const tooMany = experiments.length > RECENT_EXPERIMENT_LIMIT;
  const visibleExperiments = showAll || !tooMany ? experiments : recentExperiments;
  const options = visibleExperiments.map(formatOption);

  const selectExperiment = (id) => {
    setSelectedId(id);
    onSelect(id, experiments.find((e) => e._id === id));
  };

  if (error) return <Message severity="error" text={error} />;

  return (
    <div className="doe-row">
      <Dropdown
        value={selectedId}
        options={options}
        optionLabel="label"
        optionValue="_id"
        onChange={(e) => selectExperiment(e.value)}
        placeholder={loading ? "Loading experiments..." : "Select an experiment"}
        loading={loading}
        filter
        emptyMessage="No completed experiments found."
        style={{ minWidth: "420px" }}
      />
      {!loading && tooMany && !showAll && (
        <Button
          label={`Show all ${experiments.length} experiments (showing last 24h)`}
          link
          size="small"
          onClick={() => setShowAll(true)}
        />
      )}
    </div>
  );
}

export default ExperimentPicker;
